<template>
  <div class="audio-player bg-gray-50 rounded-lg border border-gray-200 px-4 py-3">
    <div class="flex items-center gap-3">
      <!-- Play/Pause Button -->
      <button
        @click="togglePlayback"
        class="audio-player-play-btn w-9 h-9 rounded-full flex items-center justify-center transition-colors"
        :class="isPlaying ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'"
      >
        <svg v-if="!isPlaying" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
        </svg>
      </button>

      <!-- Waveform container -->
      <div ref="waveformRef" class="audio-player-waveform flex-1 min-h-[40px]"></div>

      <!-- Time display -->
      <div class="audio-player-time text-xs text-gray-500 font-mono whitespace-nowrap">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
    </div>

    <!-- Speed controls -->
    <div class="audio-player-controls flex items-center gap-2 pt-2">
      <span class="text-xs text-gray-400">Speed:</span>
      <button
        v-for="speed in speeds"
        :key="speed"
        @click="setSpeed(speed)"
        class="text-xs px-2 py-0.5 rounded-md transition-colors"
        :class="currentSpeed === speed
          ? 'bg-indigo-600 text-white'
          : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'"
      >
        {{ speed }}x
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

const props = defineProps<{
  src: string
}>()

const waveformRef = ref(null)
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const currentSpeed = ref(1)
const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2]

let wavesurfer: any = null

onMounted(async () => {
  const WaveSurfer = (await import('wavesurfer.js')).default
  wavesurfer = WaveSurfer.create({
    container: waveformRef.value,
    waveColor: '#c7d2fe',
    progressColor: '#6366f1',
    cursorColor: '#4f46e5',
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    height: 40,
    normalize: true,
    url: props.src,
  })

  wavesurfer.on('ready', () => {
    duration.value = wavesurfer.getDuration()
  })

  wavesurfer.on('timeupdate', (time) => {
    currentTime.value = time
  })

  wavesurfer.on('play', () => {
    isPlaying.value = true
  })

  wavesurfer.on('pause', () => {
    isPlaying.value = false
  })

  wavesurfer.on('finish', () => {
    isPlaying.value = false
  })
})

onBeforeUnmount(() => {
  if (wavesurfer) wavesurfer.destroy()
})

watch(() => props.src, (newSrc) => {
  if (wavesurfer && newSrc) {
    wavesurfer.load(newSrc)
  }
})

function togglePlayback() {
  if (wavesurfer) wavesurfer.playPause()
}

function setSpeed(speed) {
  currentSpeed.value = speed
  if (wavesurfer) wavesurfer.setPlaybackRate(speed)
}

function formatTime(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}
</script>
